import Button, { buttonClasses } from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import LoadingSpinner from './loadingSpinner';
import ReCaptchaEnterprise from './recaptcha';

const BRAND_INDIGO = '#3d3f72';
const LABEL_STYLE = { color: '#5c5c66' };

const SEND_BUTTON_SX = {
  marginTop: '10px',
  marginBottom: '10px',
  background: BRAND_INDIGO,
  color: '#fff',
  letterSpacing: 'normal',
  '&:hover': {
    background: `${BRAND_INDIGO}DD`,
    color: '#fff',
  },
  [`&.${buttonClasses.disabled}`]: {
    background: '#ddd',
    color: `${BRAND_INDIGO}55`,
  },
};

const INTRO_TEXT_SX = {
  lineHeight: '1.46429em',
  letterSpacing: 'normal',
  color: 'rgba(0, 0, 0, 0.87)',
};

const questionOptions = [
  {
    label: 'For',
    value: '100',
  },
  {
    label: 'Against',
    value: '010',
  },
  {
    label: 'Neutral',
    value: '001',
  },
];

const ownerOptions = [
  {
    label: 'Yes',
    value: 1,
  },
  {
    label: 'No',
    value: 0,
  },
];

const FeedbackSelect = ({
  label = '',
  name,
  labelId = `${name}-label`,
  onChange,
  options,
  style,
  value,
}) => (
  <FeedbackSelect.Control style={style} variant='standard'>
    <InputLabel htmlFor={name} id={label ? labelId : undefined}>
      {label}
    </InputLabel>
    <Select
      inputProps={{ id: name, name }}
      labelId={labelId}
      onChange={(event) => onChange(event.target.value)}
      value={value}
    >
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </Select>
  </FeedbackSelect.Control>
);

FeedbackSelect.Control = styled(FormControl)`
  min-width: inherit;
  width: 100%;
  margin-right: 20px !important;
`;

const FeedbackTextField = ({
  onChange,
  variant = 'standard',
  ...textFieldProps
}) => (
  <FeedbackTextField.Wrapper>
    <TextField
      onChange={(event) => onChange(event.target.value)}
      slotProps={{ inputLabel: { style: LABEL_STYLE } }}
      variant={variant}
      {...textFieldProps}
    />
  </FeedbackTextField.Wrapper>
);

FeedbackTextField.Wrapper = styled.div`
  position: relative;
  margin-bottom: 15px;

  > div {
    width: 100%;
  }
`;

const FeedbackPanel = ({
  handleSubmit,
  fetchTaxingUnitPollQuestions,
  unitId,
  year,
  pushToast,
}) => {
  const [comment, setComment] = useState('');
  const [isOwner, setIsOwner] = useState('');
  const [captchaOk, setCaptchaOk] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [isLoadQuestion, setLoadedQuestion] = useState(null);
  const [responses, setResponses] = useState({});
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [submitStatus, setSubmitStatus] = useState('');
  const isDisabled =
    !captchaOk || isOwner === '' || !comment || !email || !phone;

  const fetchPollQuestions = async () => {
    try {
      if (unitId) {
        setLoadedQuestion(true);
        const res = await fetchTaxingUnitPollQuestions({ id: unitId, year });
        setLoadedQuestion(false);
        if (res) {
          setQuestions(res.results);
        } else {
          setQuestions([]);
        }
      }
    } catch (e) {
      pushToast({ error: e, entity: 'Fetching Feedback Questions' });
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: parents pass fetchTaxingUnitPollQuestions via .bind() on every render; refetch only when unitId/year change
  useEffect(() => {
    fetchPollQuestions();
  }, [unitId, year]);

  return (
    <div>
      <FeedbackPanel.Title as='h3'>
        Provide Feedback to Your Taxing Authority
      </FeedbackPanel.Title>
      <form
        onSubmit={async (ev) => {
          ev.preventDefault();
          setSubmitStatus('');
          try {
            const mapedQuestions = Object.entries(responses).reduce(
              (acc, [questionID, value]) => {
                acc.push({
                  questionID,
                  response: {
                    responseFor: Number(value[0]),
                    responseAgainst: Number(value[1]),
                    responseNeutral: Number(value[2]),
                  },
                });

                return acc;
              },
              [],
            );

            handleSubmit({
              message: comment,
              isOwner,
              contactPhone: phone,
              contactEmail: email,
              questions: mapedQuestions,
            });
            setSubmitStatus('Feedback submitted successfully.');
          } catch (e) {
            setSubmitStatus(
              'There was an error submitting your feedback. Please try again.',
            );
            pushToast({ error: e, entity: 'Sending Feedback Questions' });
          }
        }}
      >
        <div style={{ background: '#e2e2ff', padding: '10px 20px' }}>
          <Typography gutterBottom sx={INTRO_TEXT_SX} variant='body2'>
            As part of the new Texas tax transparency laws, we are providing the
            ability for taxpayers to submit comments to their taxing units. Take
            advantage of this by providing input in regards to adopted budgets,
            hearings, tax rates, and any other topic regarding property taxes in
            your area.
          </Typography>
          <Typography gutterBottom sx={INTRO_TEXT_SX} variant='body2'>
            We ask that all comments remain professional and courteous. Upon
            submission, your comment will be forwarded to taxing unit
            representatives.
          </Typography>
        </div>
        <div>
          <FeedbackPanel.Title as='h4'>Comment</FeedbackPanel.Title>
          <FeedbackPanel.InputWrapper>
            <FeedbackTextField
              id='feedback-comment'
              label='Type your comment here...'
              multiline
              onChange={setComment}
              value={comment}
              variant='outlined'
            />
          </FeedbackPanel.InputWrapper>
          <FeedbackPanel.Title as='h4'>Feedback Questions</FeedbackPanel.Title>
          {isLoadQuestion ? (
            <div role='status' style={{ textAlign: 'center' }}>
              <LoadingSpinner
                aria-label='Loading feedback questions'
                style={{ marginTop: 15, display: 'inline-block' }}
              />
            </div>
          ) : (
            <FeedbackPanel.Questions>
              {questions.map(({ question, taxingUnitPollQuestionID }, i) => (
                <FeedbackPanel.Question key={taxingUnitPollQuestionID}>
                  <FeedbackPanel.li $i={i + 1}>{question}</FeedbackPanel.li>
                  <FeedbackSelect
                    label={`Response to question ${i + 1}`}
                    name={`poll-question-${taxingUnitPollQuestionID}`}
                    onChange={(value) =>
                      setResponses((v) => ({
                        ...v,
                        [taxingUnitPollQuestionID]: value,
                      }))
                    }
                    options={questionOptions}
                    style={{ marginTop: -13, width: 100 }}
                    value={responses[taxingUnitPollQuestionID] || ''}
                  />
                </FeedbackPanel.Question>
              ))}
            </FeedbackPanel.Questions>
          )}
        </div>
        <FeedbackPanel.Contacts>
          <FeedbackTextField
            id='feedback-phone'
            label='Contact Phone Number'
            onChange={setPhone}
            required
            type='tel'
            value={phone}
          />
          <FeedbackTextField
            id='feedback-email'
            label='Contact Email'
            onChange={setEmail}
            required
            type='email'
            value={email}
          />
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <label
              htmlFor='property-owner'
              id='property-owner-label'
              style={{ whiteSpace: 'nowrap', marginRight: 20 }}
            >
              Are you the owner of this property?
            </label>
            <FeedbackSelect
              name='property-owner'
              onChange={setIsOwner}
              options={ownerOptions}
              value={isOwner}
            />
          </div>
        </FeedbackPanel.Contacts>
        <FeedbackPanel.ButtonWrapper>
          <ReCaptchaEnterprise
            onChange={() => setCaptchaOk(true)}
            onError={() => setCaptchaOk(false)}
          />
          <div style={{ marginLeft: 20 }}>
            <Button
              disabled={isDisabled}
              sx={SEND_BUTTON_SX}
              type='submit'
              variant='contained'
            >
              SEND FEEDBACK
            </Button>
          </div>
        </FeedbackPanel.ButtonWrapper>
        <div
          aria-live='polite'
          role='status'
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            overflow: 'hidden',
            clip: 'rect(0,0,0,0)',
          }}
        >
          {submitStatus}
        </div>
      </form>
    </div>
  );
};

FeedbackPanel.Title = styled.div`
  font-size: 15px;
  font-weight: bold;
  margin: 0 0 10px 0;
`;

FeedbackPanel.li = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  margin: 20px 0;
  word-break: break-all;

  &::before {
    content: '${(props) => props.$i}.';
    position: absolute;
    margin-left: -20px;
    align-self: flex-start;
  }

  &::after {
    content: ' ';
    flex: 1 1 50px;
    background-image: linear-gradient(to right, #333 40%, rgba(255, 255, 255, 0) 20%);
    background-position: top;
    background-size: 10px 1px;
    background-repeat: repeat-x;
    display: block;
    height: 1px;
    margin: 0 15px;
  }
`;

FeedbackPanel.Question = styled.div`
  display: flex;
`;

FeedbackPanel.Questions = styled.div`
  padding: 0 20px;
`;

FeedbackPanel.InputWrapper = styled.div`
  fieldset {
    border-color: #333fb8 !important;
  }

  .MuiOutlinedInput-root {
    background-color: rgb(239, 239, 255);
  }
`;

FeedbackPanel.Contacts = styled.div`
  display: flex;
  padding: 16px;
  align-items: flex-start;
  flex-wrap: wrap;

  > div {
    &:last-child {
      margin-right: 20px;
    }

    flex-grow: 1;
    flex-shrink: 1;
    margin-right: 40px;
  }
`;

FeedbackPanel.ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export default FeedbackPanel;
